import React, { useState, useEffect, useCallback } from 'react';
import { Save, Loader2, Plus, Clock, Settings, ChevronDown, X, RefreshCw } from 'lucide-react';
import { toast } from 'sonner';
import { api } from '../../api/client';
import { useAuth } from '../../contexts/AuthContext';
import ConfirmModal from '../../components/ui/ConfirmModal';
import { calendarDayString, formatCalendarDate, todayCalendarDate } from '../../lib/calendar-date';
import { useTenantTimeZone } from '../../hooks/useTenantTimeZone';
import { unwrap, unwrapArray } from '../../lib/api-envelope';
import { ClockRoundingSection } from './ClockRoundingSection';
import { SettingsCard, SettingsColumn, SettingsGrid, SettingsHelp, SettingsNotice, SettingsPage } from '../../components/settings/SettingsPage';
import { Tooltip } from '../../components/ui/tooltip';

// ─── Types ────────────────────────────────────────────────────────
// Editable config fields. `frequency` is null until the server sends one or the
// admin picks one — it must never be preselected (see loadConfig below).
interface PayPeriodConfigForm {
  frequency: string | null;
  anchor_date: string;
  pay_date_offset_days: number;
  // Null until the server sends one or Bi-Monthly is selected (then it shows a
  // visible, editable 15 rather than relying on the API's invisible default).
  semi_monthly_split_day: number | null;
}

// Config fields this form does not edit but must not clobber on save. Only ever
// populated by a successful GET that returned a row.
//
// `week_start_day` is here, not on the form: nothing on the API reads it — the
// period generator walks from anchor_date alone — so a weekly period starts on
// the anchor's weekday whatever this says. But PUT /pay-periods/config is an
// upsert that writes `week_start_day ?? 1`, so leaving it out of the payload
// would overwrite a stored value with Monday. It is round-tripped instead.
interface PreservedConfigFields {
  custom_days?: number;
  week_start_day?: number;
}

// loading          — GET in flight
// configured       — GET succeeded and returned a config row
// unconfigured     — GET succeeded and returned NO row (nothing is preselected)
// error            — GET failed; the form is NOT usable and Save stays disabled
type ConfigStatus = 'loading' | 'configured' | 'unconfigured' | 'error';

// Shape returned by GET /pay-periods (SELECT * FROM pay_periods) — snake_case DB columns.
interface PayPeriod {
  id: string;
  period_number: number;
  period_start: string;
  period_end: string;
  pay_date: string | null;
  status: string;
  worker_count: number | null;
  total_regular_hours: string | number | null;
  total_overtime_hours: string | number | null;
}

interface OvertimeRule {
  id: string;
  vertical: string;
  rule_name: string;
  preset_key: string;
  daily_threshold: number | null;
  weekly_threshold: number | null;
  monthly_threshold: number | null;
}

interface OTPreset {
  key: string;
  label: string;
  description: string;
}

// Labels are display-only. The stored/API value for Bi-Monthly stays 'semi_monthly'
// — the DB CHECK and the period generator both key off that string.
const FREQUENCIES = [
  { value: 'weekly', label: 'Weekly' },
  { value: 'biweekly', label: 'Biweekly' },
  { value: 'semi_monthly', label: 'Bi-Monthly' },
  { value: 'monthly', label: 'Monthly' },
];

// Which inputs actually affect each frequency. Pay date offset applies to all
// four, so it is not listed here. Weekly shows the anchor date because the
// anchor's weekday IS the week start — the generator adds 7 days to it.
const FIELDS_FOR_FREQUENCY: Record<string, { anchor: boolean; splitDay: boolean; stepDays?: number }> = {
  weekly: { anchor: true, splitDay: false, stepDays: 7 },
  biweekly: { anchor: true, splitDay: false, stepDays: 14 },
  semi_monthly: { anchor: false, splitDay: true },
  monthly: { anchor: false, splitDay: false },
};

const DEFAULT_SPLIT_DAY = 15;

const WEEKDAY_NAMES = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];

const OT_PRESETS: OTPreset[] = [
  { key: 'ab_standard', label: 'AB Standard', description: '8h/day or 44h/week' },
  { key: 'ab_oilwell', label: 'AB Oilwell', description: '12h/day or 191h/month' },
  { key: 'ab_trucking', label: 'AB Trucking', description: '10h/day or 50h/week' },
  { key: 'bc_standard', label: 'BC Standard', description: '8h/day or 40h/week' },
  { key: 'tx_flsa', label: 'TX FLSA', description: '40h/week (federal)' },
  { key: 'nd_oilfield', label: 'ND Oilfield', description: '12h/day or 40h/week' },
  { key: 'sk_standard', label: 'SK Standard', description: '8h/day or 40h/week' },
  { key: 'none', label: 'No OT Rules', description: 'No automatic overtime' },
];

// Integer coercion that keeps 0 (both week_start_day and pay_date_offset_days
// accept 0, so `Number(x) || fallback` silently corrupts them).
function intOr(v: unknown, fallback: number): number {
  if (v == null || v === '') return fallback;
  const n = Number(v);
  return Number.isFinite(n) ? Math.trunc(n) : fallback;
}

// A DATE column, in either spelling the API sends; the calendar day only (no TZ shift).
function toDateOnly(v: unknown): string {
  return typeof v === 'string' ? (calendarDayString(v) ?? v) : '';
}

// The TENANT's today (companies.time_zone). '' only seeds the anchor input:
// with no zone there is no honest today, and an empty anchor blocks the save.
function todayStr(timeZone: string | null): string {
  return todayCalendarDate(timeZone) ?? '';
}

function parseDateStr(s: string): Date | null {
  const [y, m, d] = (s || '').slice(0, 10).split('-').map(Number);
  if (!y || !m || !d) return null;
  const dt = new Date(y, m - 1, d);
  return Number.isNaN(dt.getTime()) ? null : dt;
}

function weekdayName(dateStr: string): string | null {
  const dt = parseDateStr(dateStr);
  return dt ? WEEKDAY_NAMES[dt.getDay()] : null;
}

function monthLabel(dateStr: string): string {
  return formatCalendarDate(dateStr, { month: 'long', year: 'numeric' }, 'en-US') ?? dateStr;
}

// anchor_date is NOT NULL in the DB and the API rejects a save without it, but only
// Weekly and Biweekly expose it. For the other two the value is displayed below
// the fields, so nothing is silently invented:
//   semi_monthly — generator uses the month only (day of month is ignored)
//   monthly      — generator uses the month only
function effectiveAnchor(form: PayPeriodConfigForm, timeZone: string | null): string {
  return form.anchor_date || todayStr(timeZone);
}

// Real hours columns are total_regular_hours + total_overtime_hours (DECIMAL → string over JSON).
function periodHours(p: PayPeriod): { total: number; regular: number; overtime: number } | null {
  const hasAny = p.total_regular_hours != null || p.total_overtime_hours != null;
  if (!hasAny) return null;
  const regular = numOrZero(p.total_regular_hours);
  const overtime = numOrZero(p.total_overtime_hours);
  return { total: regular + overtime, regular, overtime };
}

function numOrZero(v: unknown): number {
  const n = Number(v);
  return Number.isFinite(n) ? n : 0;
}

function StatusPill({ status }: { status: string }) {
  // pay_period_status is exactly (open, approved, exported, paid).
  //
  // 'locked' was never one of them. It is the label PATCH /pay-periods/:id/lock
  // wrote -- a guaranteed 22P02 that never once succeeded -- and the route was
  // deleted in API #446. Nothing can arrive here carrying it.
  //
  // 'approved' and 'paid' were both MISSING, and the fallthrough is what makes
  // that expensive: `styles[status] || styles.open` painted them amber, so a
  // closed-out period rendered in the same colour as one still taking entries.
  // An unstyled status is not a cosmetic gap on a payroll screen.
  //
  // The badge idiom of the re-skinned registers: a saturated signal fill with
  // dark ink, which holds in both themes. The pale wash with a light ink of the
  // same hue that this used to be read about 2.4:1 on the light card. Three
  // hues for four statuses, grouped by meaning: open is still taking entries
  // (amber); approved and exported are closed and on their way (blue, the
  // label separates them); paid is done (green).
  const fills: Record<string, string> = {
    open: 'bg-fi-signal-amber',
    approved: 'bg-fi-signal-blue',
    exported: 'bg-fi-signal-blue',
    paid: 'bg-fi-signal-green',
  };
  return (
    <span className={`inline-flex w-fit items-center rounded-fi-pill border border-black/20 px-2 py-0.5 text-[0.68rem] font-black uppercase tracking-[0.06em] leading-none text-fi-signal-ink ${fills[status] || fills.open}`}>
      {status.charAt(0).toUpperCase() + status.slice(1)}
    </span>
  );
}

export default function PayPeriodSettingsScreen() {
  const { user } = useAuth();
  /*
   * STRICT admin. Every write on this page — PUT /pay-periods/config, generate,
   * regenerate, and every /overtime-rules write — is
   * requireRole('admin', { strict: true }): role `admin` literally. Not owner,
   * and NOT a dispatcher with dispatcher_admin_access, which is why this is not
   * useCanWriteCompanySettings. The three GETs are ungated, so anyone who
   * reaches the page by URL sees the settings read-only.
   */
  const canWrite = user?.role === 'admin';
  const timeZone = useTenantTimeZone();

  // Config state — frequency starts null so nothing is ever preselected.
  // anchor_date seeds to today only as an editable starting point (NOT NULL in
  // DB, no DB default); it is visible in the date input, unlike a frequency
  // pill that would read as a saved choice.
  const [config, setConfig] = useState<PayPeriodConfigForm>({
    frequency: null,
    anchor_date: todayStr(timeZone),
    pay_date_offset_days: 5,
    semi_monthly_split_day: null,
  });
  const [configStatus, setConfigStatus] = useState<ConfigStatus>('loading');
  const [configError, setConfigError] = useState<string | null>(null);
  // Fields this form never edits. Populated ONLY by a GET that returned a row,
  // so a save can never invent or drop them.
  const [preserved, setPreserved] = useState<PreservedConfigFields>({});
  const [saving, setSaving] = useState(false);
  // Errors only. Success is a toast; a line on the page is kept for something that went wrong.
  const [saveMsg, setSaveMsg] = useState<{ type: 'error'; text: string } | null>(null);

  // Generate
  const [generateCount, setGenerateCount] = useState(12);
  const [generating, setGenerating] = useState(false);
  const [regenerating, setRegenerating] = useState(false);

  // Periods
  const [periods, setPeriods] = useState<PayPeriod[]>([]);
  const [periodsLoading, setPeriodsLoading] = useState(true);
  const [showAll, setShowAll] = useState(false);

  // OT rules
  const [otRules, setOtRules] = useState<OvertimeRule[]>([]);
  const [otLoading, setOtLoading] = useState(true);
  const [presetModal, setPresetModal] = useState<{ vertical: string } | null>(null);
  const [applyingPreset, setApplyingPreset] = useState(false);
  // Regenerate: skipped periods + force confirm
  const [skippedPeriods, setSkippedPeriods] = useState<Array<{ period_id: string; period_number: number; reason: string; counts: Record<string, number> }>>([]);
  const [showForceRegenConfirm, setShowForceRegenConfirm] = useState(false);
  const [forceRegenerating, setForceRegenerating] = useState(false);

  // Fetch config
  const loadConfig = useCallback(async () => {
    setConfigStatus('loading');
    setConfigError(null);
    try {
      const res = await api.payPeriodConfig.get();
      // GET /pay-periods/config answers { success, data } and data is null when
      // the company has no config row — unwrap the envelope explicitly so a null
      // payload can't be mistaken for a config object. This was hand-rolled as
      // an `'data' in body` presence test, which is the one shape the helper's
      // docblock calls out as invisible: a handler returning `data: undefined`
      // ships `{ success: true }` with the key dropped, the test reads that as
      // "not an envelope", and the envelope reaches `c` after all.
      const c = unwrap<any>(res.data);

      if (c && c.frequency) {
        setConfig({
          frequency: c.frequency,
          anchor_date: toDateOnly(c.anchor_date) || todayStr(timeZone),
          pay_date_offset_days: intOr(c.pay_date_offset_days, 5),
          semi_monthly_split_day: c.semi_monthly_split_day != null ? intOr(c.semi_monthly_split_day, DEFAULT_SPLIT_DAY) : null,
        });
        setPreserved({
          ...(c.custom_days != null ? { custom_days: intOr(c.custom_days, 1) } : {}),
          ...(c.week_start_day != null ? { week_start_day: intOr(c.week_start_day, 1) } : {}),
        });
        setConfigStatus('configured');
      } else {
        // No config row for this company. Preselect NOTHING — the admin has to
        // choose a frequency before Save is enabled.
        setPreserved({});
        setConfigStatus('unconfigured');
      }
    } catch (err: any) {
      console.error('Failed to load pay period config', err);
      setPreserved({});
      setConfigError(err?.response?.data?.error || err?.message || 'The settings could not be loaded.');
      setConfigStatus('error');
    }
  }, [timeZone]);

  useEffect(() => { loadConfig(); }, [loadConfig]);

  // Fetch periods
  const fetchPeriods = useCallback(async () => {
    setPeriodsLoading(true);
    try {
      const res = await api.payPeriodConfig.listPeriods();
      setPeriods(unwrapArray<PayPeriod>(res.data));
    } catch (err) {
      console.error('Failed to load periods', err);
    } finally {
      setPeriodsLoading(false);
    }
  }, []);

  useEffect(() => { fetchPeriods(); }, [fetchPeriods]);

  // Fetch OT rules
  const fetchOtRules = useCallback(async () => {
    setOtLoading(true);
    try {
      const res = await api.payPeriodConfig.listOvertimeRules();
      setOtRules(unwrapArray<OvertimeRule>(res.data));
    } catch (err) {
      console.error('Failed to load OT rules', err);
    } finally {
      setOtLoading(false);
    }
  }, []);

  useEffect(() => { fetchOtRules(); }, [fetchOtRules]);

  // Which inputs are relevant to the chosen frequency (none until one is chosen).
  const shownFields = (config.frequency && FIELDS_FOR_FREQUENCY[config.frequency]) || null;
  // The split day the admin is actually looking at for Bi-Monthly.
  const splitDay = config.semi_monthly_split_day ?? DEFAULT_SPLIT_DAY;

  // Save is only possible once we know what the server has (or doesn't have)
  // AND the admin has an explicit frequency. A failed load never saves.
  const canSave =
    !saving &&
    (configStatus === 'configured' || configStatus === 'unconfigured') &&
    !!config.frequency &&
    !!effectiveAnchor(config, timeZone);

  // Save config
  const handleSaveConfig = async () => {
    if (configStatus === 'loading' || configStatus === 'error') {
      setSaveMsg({ type: 'error', text: 'Settings have not loaded — nothing was sent.' });
      return;
    }
    if (!config.frequency) {
      setSaveMsg({ type: 'error', text: 'Choose a pay frequency first.' });
      return;
    }
    const anchor = effectiveAnchor(config, timeZone);
    if (!anchor) {
      setSaveMsg({ type: 'error', text: 'Anchor date is required.' });
      return;
    }
    setSaving(true);
    setSaveMsg(null);
    try {
      // Coerce integer fields to ensure they aren't sent as strings.
      // Field names match the API's upsertConfig / DB columns exactly. `timezone`
      // is deliberately absent: the column was dropped in API migration 0325 and
      // the tenant zone lives on companies.time_zone, edited in Regional & Tax.
      const payload: Record<string, any> = {
        frequency: config.frequency,
        anchor_date: anchor,
        pay_date_offset_days: intOr(config.pay_date_offset_days, 5),
      };
      // Bi-Monthly owns the split day and sends what's on screen. Any other
      // frequency round-trips the stored value untouched — omitting it would let
      // the API's `?? 15` default overwrite a company's real boundary.
      if (config.semi_monthly_split_day != null) {
        payload.semi_monthly_split_day = config.frequency === 'semi_monthly'
          ? splitDay
          : config.semi_monthly_split_day;
      } else if (config.frequency === 'semi_monthly') {
        payload.semi_monthly_split_day = splitDay;
      }
      if (preserved.custom_days != null) {
        payload.custom_days = preserved.custom_days;
      }
      if (preserved.week_start_day != null) {
        payload.week_start_day = preserved.week_start_day;
      }
      const res = await api.payPeriodConfig.update(payload);
      // PUT returns the saved row — adopt the fields we preserve so a follow-up
      // save round-trips whatever the DB defaulted them to.
      const body: any = res?.data;
      const saved = body && typeof body === 'object' && 'data' in body ? body.data : body;
      if (saved && saved.frequency) {
        setConfig(c => ({
          ...c,
          anchor_date: toDateOnly(saved.anchor_date) || c.anchor_date,
          semi_monthly_split_day: saved.semi_monthly_split_day != null
            ? intOr(saved.semi_monthly_split_day, DEFAULT_SPLIT_DAY)
            : c.semi_monthly_split_day,
        }));
        setPreserved({
          ...(saved.custom_days != null ? { custom_days: intOr(saved.custom_days, 1) } : {}),
          ...(saved.week_start_day != null ? { week_start_day: intOr(saved.week_start_day, 1) } : {}),
        });
      }
      setConfigStatus('configured');
      toast.success('Pay period settings saved.');
    } catch (err: any) {
      setSaveMsg({ type: 'error', text: err.response?.data?.error || 'Failed to save settings.' });
    } finally {
      setSaving(false);
    }
  };

  // Generate periods
  const handleGenerate = async () => {
    setGenerating(true);
    try {
      await api.payPeriodConfig.generate({ count: generateCount });
      await fetchPeriods();
      toast.success(`Generated ${generateCount} pay periods.`);
    } catch (err: any) {
      setSaveMsg({ type: 'error', text: err.response?.data?.error || 'Failed to generate periods.' });
    } finally {
      setGenerating(false);
    }
  };

  // Regenerate periods (delete open + recreate from current config)
  const handleRegenerate = async () => {
    setRegenerating(true);
    setSkippedPeriods([]);
    try {
      const res = await api.payPeriodConfig.regenerate({ count: generateCount });
      const data = unwrap<{ regenerated?: any[]; skipped?: any[] }>(res.data);
      await fetchPeriods();

      if (!data) {
        // 201 with no payload. The regenerate itself committed, so reporting
        // "0 periods regenerated" would be a lie; the refetched table above is
        // the honest answer and the admin is pointed at it.
        setSaveMsg({ type: 'error', text: 'Periods were regenerated but the server returned no summary — check the list below.' });
        return;
      }

      const regenerated: any[] = data.regenerated || [];
      const skipped: any[] = data.skipped || [];

      if (skipped.length > 0) {
        setSkippedPeriods(skipped);
        // The skipped periods get their own line on the page, with the
        // force-regenerate action; the toast reports only what went through.
        toast.success(`${regenerated.length} periods regenerated.`);
      } else {
        toast.success(`${regenerated.length} periods regenerated.`);
      }
    } catch (err: any) {
      if (err.response?.status === 403) {
        toast.error('Only admins can regenerate pay periods.');
      } else {
        setSaveMsg({ type: 'error', text: err.response?.data?.error || 'Failed to regenerate periods.' });
      }
    } finally {
      setRegenerating(false);
    }
  };

  const handleForceRegenerate = async () => {
    setForceRegenerating(true);
    try {
      const res = await api.payPeriodConfig.regenerate({ count: generateCount, force: true });
      const data = unwrap<{ regenerated?: any[] }>(res.data);
      await fetchPeriods();
      setSkippedPeriods([]);
      setShowForceRegenConfirm(false);
      if (!data) {
        setSaveMsg({ type: 'error', text: 'Periods were regenerated but the server returned no summary — check the list below.' });
        return;
      }
      const regenerated: any[] = data.regenerated || [];
      toast.success(`${regenerated.length} periods regenerated (including previously skipped).`);
    } catch (err: any) {
      toast.error(err.response?.data?.error || 'Failed to force-regenerate periods.');
      setShowForceRegenConfirm(false);
    } finally {
      setForceRegenerating(false);
    }
  };

  // Apply OT preset
  const handleApplyPreset = async (presetKey: string) => {
    if (!presetModal) return;
    setApplyingPreset(true);
    try {
      await api.payPeriodConfig.applyOvertimePreset(presetKey, { vertical: presetModal.vertical });
      await fetchOtRules();
      setPresetModal(null);
    } catch (err: any) {
      setSaveMsg({ type: 'error', text: err.response?.data?.error || 'Failed to apply preset.' });
    } finally {
      setApplyingPreset(false);
    }
  };

  const displayPeriods = showAll ? periods : periods.slice(0, 10);

  const skippedNums = skippedPeriods.map(sp => `#${sp.period_number}`).join(', ');

  return (
    // The Finance & region workspace renders the title and the tab strip.
    <SettingsPage>
      {!canWrite && (
        <SettingsHelp>Only an admin can change pay periods and overtime rules.</SettingsHelp>
      )}

      {saveMsg && <SettingsNotice tone="danger">{saveMsg.text}</SettingsNotice>}

      <SettingsGrid>
        {/* ═══ SECTION 1: Pay Period Configuration ═══ */}
        <SettingsCard title="Pay period settings" icon={<Settings size={18} />}>
          {configStatus === 'loading' ? (
            <div className="flex justify-center py-12">
              <Loader2 size={24} className="animate-spin text-fieldiq-accent" />
            </div>
          ) : (
            <>
              {/* Load failed — the fields are deliberately NOT rendered. Showing an
                  editable form here is what let a default frequency get saved over
                  a company's real settings. */}
              {configStatus === 'error' && (
                <SettingsNotice
                  tone="danger"
                  action={canWrite && (
                    <button
                      onClick={loadConfig}
                      className="flex items-center gap-1.5 rounded-lg border border-fieldiq-border bg-fieldiq-card px-3 py-1.5 text-xs font-semibold text-fieldiq-text hover:border-fieldiq-accent/50"
                    >
                      <RefreshCw size={14} /> Retry
                    </button>
                  )}
                >
                  Couldn't load pay period settings{configError ? ` (${configError})` : ''}, so Save is off until they load.
                </SettingsNotice>
              )}

              {/* No config row for this company */}
              {configStatus === 'unconfigured' && (
                <SettingsNotice tone="warning">No pay period settings are saved yet; choose a frequency below.</SettingsNotice>
              )}

              {configStatus !== 'error' && (
                <>
              {/* Frequency */}
              <div>
                <label className="block text-sm font-medium text-white mb-2">
                  Frequency
                  {!config.frequency && <span className="ml-2 text-xs font-normal text-fieldiq-accent-signal">Not set — choose one</span>}
                </label>
                <div className="flex rounded-lg overflow-hidden border border-fieldiq-border">
                  {FREQUENCIES.map((f) => (
                    <button
                      key={f.value}
                      onClick={() => setConfig(c => ({ ...c, frequency: f.value }))}
                      disabled={!canWrite}
                      aria-pressed={config.frequency === f.value}
                      className={`flex-1 whitespace-nowrap px-2 py-2.5 text-sm font-medium transition-colors disabled:cursor-not-allowed ${
                        config.frequency === f.value
                          ? 'bg-fieldiq-accent text-primary-foreground'
                          : 'bg-fieldiq-dark text-fieldiq-text hover:text-white'
                      }`}
                    >
                      {f.label}
                    </button>
                  ))}
                </div>
              </div>

              {/* Only the fields that affect the chosen frequency are rendered.
                  Pay date offset always applies. */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {shownFields?.anchor && (
                  <div>
                    <label htmlFor="anchor_date" className="block text-sm font-medium text-white mb-2">Anchor date</label>
                    <input
                      id="anchor_date"
                      type="date"
                      value={config.anchor_date}
                      disabled={!canWrite}
                      onChange={(e) => setConfig(c => ({ ...c, anchor_date: e.target.value }))}
                      className="w-full bg-fieldiq-dark border border-fieldiq-border rounded-lg px-4 py-2.5 text-white text-sm focus:border-fieldiq-accent focus:outline-none"
                    />
                    <SettingsHelp className="mt-1.5">
                      First period starts on this date, then every {shownFields.stepDays} days
                      {config.frequency === 'weekly' && weekdayName(config.anchor_date)
                        ? ` — weeks start on ${weekdayName(config.anchor_date)}.`
                        : '.'}
                    </SettingsHelp>
                  </div>
                )}

                {shownFields?.splitDay && (
                  <div>
                    <label className="block text-sm font-medium text-white mb-2">
                      Split day <span className="font-normal text-fieldiq-muted">(day the first period ends)</span>
                    </label>
                    <input
                      type="number"
                      min={1}
                      max={28}
                      value={splitDay}
                      disabled={!canWrite}
                      onChange={(e) => setConfig(c => ({ ...c, semi_monthly_split_day: Math.min(28, Math.max(1, intOr(e.target.value, DEFAULT_SPLIT_DAY))) }))}
                      className="w-full bg-fieldiq-dark border border-fieldiq-border rounded-lg px-4 py-2.5 text-white text-sm focus:border-fieldiq-accent focus:outline-none"
                    />
                    <SettingsHelp className="mt-1.5">
                      Periods run the 1st–{splitDay} and {splitDay + 1}–end of month.
                    </SettingsHelp>
                  </div>
                )}

                <div>
                  <label className="block text-sm font-medium text-white mb-2">
                    Pay date offset <span className="font-normal text-fieldiq-muted">(days after period end)</span>
                  </label>
                  <input
                    type="number"
                    min={0}
                    max={14}
                    value={config.pay_date_offset_days}
                    disabled={!canWrite}
                    onChange={(e) => setConfig(c => ({ ...c, pay_date_offset_days: Math.min(14, Math.max(0, intOr(e.target.value, 0))) }))}
                    className="w-full bg-fieldiq-dark border border-fieldiq-border rounded-lg px-4 py-2.5 text-white text-sm focus:border-fieldiq-accent focus:outline-none"
                  />
                </div>

              </div>

              {/* Frequencies that don't expose an anchor date still need one stored.
                  Show the value that will be saved rather than hiding it. */}
              {config.frequency && !shownFields?.anchor && (
                <SettingsHelp>
                  {config.frequency === 'semi_monthly' && (
                    <>Periods follow the calendar month, starting {monthLabel(effectiveAnchor(config, timeZone))}.</>
                  )}
                  {config.frequency === 'monthly' && (
                    <>Periods run whole calendar months, starting {monthLabel(effectiveAnchor(config, timeZone))}.</>
                  )}
                </SettingsHelp>
              )}
                </>
              )}

              {/* Save */}
              {canWrite && (<>
              <div className="flex justify-end items-center gap-3 pt-2">
                <Tooltip content={
                    configStatus === 'error'
                      ? 'Settings could not be loaded'
                      : !config.frequency
                        ? 'Choose a pay frequency first'
                        : undefined
                  }>
                  <button
                    onClick={handleSaveConfig}
                    disabled={!canSave}
                    className="flex items-center gap-2 bg-fieldiq-accent text-primary-foreground font-semibold px-5 py-2.5 rounded-lg hover:bg-fieldiq-accent/90 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {saving ? <Loader2 size={16} className="animate-spin" /> : <Save size={16} />}
                    {saving ? 'Saving...' : 'Save Settings'}
                  </button>
                </Tooltip>
              </div>

              {/* Generate section */}
              <div className="border-t border-fieldiq-border pt-5 mt-2 space-y-3">
                <h3 className="text-sm font-semibold text-white">Generate periods</h3>
                <div className="flex flex-wrap items-center gap-3">
                  <input
                    type="number"
                    min={1}
                    max={24}
                    value={generateCount}
                    onChange={(e) => setGenerateCount(Math.min(24, Math.max(1, parseInt(e.target.value) || 1)))}
                    className="w-24 bg-fieldiq-dark border border-fieldiq-border rounded-lg px-3 py-2.5 text-white text-sm focus:border-fieldiq-accent focus:outline-none"
                  />
                  <span className="text-sm text-fieldiq-text">periods</span>
                  <button
                    onClick={handleGenerate}
                    disabled={generating || regenerating}
                    className="flex items-center gap-2 bg-fieldiq-card border border-fieldiq-border text-white font-medium px-4 py-2.5 rounded-lg hover:border-fieldiq-accent/50 transition-colors disabled:opacity-50"
                  >
                    {generating ? <Loader2 size={16} className="animate-spin" /> : <Plus size={16} />}
                    {generating ? 'Generating...' : 'Generate'}
                  </button>
                  <button
                    onClick={handleRegenerate}
                    disabled={generating || regenerating}
                    className="flex items-center gap-2 bg-fieldiq-card border border-amber-500/60 text-white font-medium px-4 py-2.5 rounded-lg hover:bg-amber-500/10 transition-colors disabled:opacity-50"
                  >
                    {regenerating ? <Loader2 size={16} className="animate-spin" /> : <RefreshCw size={16} />}
                    {regenerating ? 'Regenerating...' : 'Regenerate'}
                  </button>
                </div>
                <SettingsHelp>Regenerate removes open periods and recreates them from your current settings.</SettingsHelp>
                {/* Which periods and what they hold is listed in the confirm
                    dialog the action opens, so this stays one line. */}
                {skippedPeriods.length > 0 && (
                  <SettingsNotice
                    tone="warning"
                    action={
                      <button
                        onClick={() => setShowForceRegenConfirm(true)}
                        className="rounded-lg border border-fieldiq-border bg-fieldiq-card px-3 py-1.5 text-xs font-semibold text-fieldiq-text hover:border-red-500/60"
                      >
                        Force-regenerate…
                      </button>
                    }
                  >
                    {skippedPeriods.length} period{skippedPeriods.length > 1 ? 's' : ''} skipped ({skippedNums}): they hold timesheet data.
                  </SettingsNotice>
                )}
              </div>
              </>)}
            </>
          )}
        </SettingsCard>

        <SettingsColumn>
          {/* ═══ Clock rounding (api#823) — decides the hours of record, so it sits beside the OT rules ═══ */}
          <ClockRoundingSection canWrite={canWrite} />

          {/* ═══ SECTION 3: Overtime Rules ═══ */}
          <SettingsCard title="Overtime rules" icon={<Clock size={18} />}>
            {otLoading ? (
              <div className="flex justify-center py-12">
                <Loader2 size={24} className="animate-spin text-fieldiq-accent" />
              </div>
            ) : otRules.length === 0 ? (
              <div className="text-center py-8 text-fieldiq-muted text-sm">
                No overtime rules configured. Rules are set per vertical your company has enabled.
              </div>
            ) : (
              otRules.map((rule) => (
                <div key={rule.id} className="bg-fieldiq-dark rounded-lg border border-fieldiq-border p-4">
                  <div className="flex items-start justify-between gap-4">
                    <div>
                      <h3 className="text-sm font-semibold text-white">{rule.vertical}</h3>
                      <p className="text-sm text-fieldiq-text mt-1">{rule.rule_name || 'No rule set'}</p>
                      <div className="flex gap-4 mt-2 text-xs text-fieldiq-muted">
                        {rule.daily_threshold != null && <span>Daily: {rule.daily_threshold}h</span>}
                        {rule.weekly_threshold != null && <span>Weekly: {rule.weekly_threshold}h</span>}
                        {rule.monthly_threshold != null && <span>Monthly: {rule.monthly_threshold}h</span>}
                      </div>
                    </div>
                    {canWrite && <button
                      onClick={() => setPresetModal({ vertical: rule.vertical })}
                      className="text-xs bg-fieldiq-card border border-fieldiq-border text-fieldiq-text font-medium px-3 py-1.5 rounded-lg hover:border-fieldiq-accent/50 transition-colors whitespace-nowrap"
                    >
                      Change preset
                    </button>}
                  </div>
                </div>
              ))
            )}
          </SettingsCard>
        </SettingsColumn>

        {/* ═══ SECTION 2: Generated Periods Table ═══ */}
        <SettingsCard
          title="Generated periods"
          icon={<Clock size={18} />}
          wide
          flush
          actions={periods.length > 0 && <span className="text-xs text-fieldiq-muted">{periods.length} total</span>}
        >
          {periodsLoading ? (
            <div className="flex justify-center py-12">
              <Loader2 size={24} className="animate-spin text-fieldiq-accent" />
            </div>
          ) : periods.length === 0 ? (
            <div className="text-center py-12 text-fieldiq-muted text-sm">
              No periods generated yet. Configure settings above and click Generate.
            </div>
          ) : (
            <>
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b border-fieldiq-border text-fieldiq-muted text-left">
                      <th className="px-5 py-3 font-medium">#</th>
                      <th className="px-5 py-3 font-medium">Start</th>
                      <th className="px-5 py-3 font-medium">End</th>
                      <th className="px-5 py-3 font-medium">Pay date</th>
                      <th className="px-5 py-3 font-medium">Status</th>
                      <th className="px-5 py-3 font-medium text-right">Workers</th>
                      <th className="px-5 py-3 font-medium text-right">Hours</th>
                    </tr>
                  </thead>
                  <tbody>
                    {displayPeriods.map((p) => {
                      const hours = periodHours(p);
                      return (
                      <tr key={p.id} className="border-b border-fieldiq-border/50 hover:bg-fieldiq-dark/50">
                        {/* period_number is null on periods created before the config-driven generator */}
                        <td className="px-5 py-3 text-white font-medium">{p.period_number ?? '—'}</td>
                        <td className="px-5 py-3 text-fieldiq-text">{toDateOnly(p.period_start) || '—'}</td>
                        <td className="px-5 py-3 text-fieldiq-text">{toDateOnly(p.period_end) || '—'}</td>
                        <td className="px-5 py-3 text-fieldiq-text">{toDateOnly(p.pay_date) || '—'}</td>
                        <td className="px-5 py-3"><StatusPill status={p.status} /></td>
                        <td className="px-5 py-3 text-fieldiq-text text-right">{p.worker_count ?? '—'}</td>
                        <td
                          className="px-5 py-3 text-fieldiq-text text-right"
                          title={hours ? `${hours.regular.toFixed(1)} regular + ${hours.overtime.toFixed(1)} OT` : undefined}
                        >
                          {hours ? hours.total.toFixed(1) : '—'}
                        </td>
                      </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
              {periods.length > 10 && (
                <div className="px-5 py-3 border-t border-fieldiq-border">
                  <button
                    onClick={() => setShowAll(!showAll)}
                    className="text-sm text-fieldiq-accent-signal hover:underline flex items-center gap-1"
                  >
                    <ChevronDown size={14} className={showAll ? 'rotate-180 transition-transform' : 'transition-transform'} />
                    {showAll ? 'Show last 10' : `Show all ${periods.length}`}
                  </button>
                </div>
              )}
            </>
          )}
        </SettingsCard>
      </SettingsGrid>

      {/* ═══ OT Preset Modal ═══ */}
      {presetModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60">
          <div className="bg-card border border-border rounded-2xl p-5 w-full max-w-md shadow-xl mx-4">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-white font-semibold">Choose OT preset — {presetModal.vertical}</h3>
              <button onClick={() => setPresetModal(null)} className="text-fieldiq-muted hover:text-white">
                <X size={18} />
              </button>
            </div>
            <div className="space-y-2 max-h-80 overflow-y-auto">
              {OT_PRESETS.map((preset) => (
                <button
                  key={preset.key}
                  onClick={() => handleApplyPreset(preset.key)}
                  disabled={applyingPreset}
                  className="w-full text-left bg-background border border-border rounded-lg px-4 py-3 hover:border-fieldiq-accent transition-colors disabled:opacity-50"
                >
                  <p className="text-sm font-medium text-white">{preset.label}</p>
                  <p className="text-xs text-fieldiq-muted mt-0.5">{preset.description}</p>
                </button>
              ))}
            </div>
            {applyingPreset && (
              <div className="flex justify-center mt-4">
                <Loader2 size={20} className="animate-spin text-fieldiq-accent" />
              </div>
            )}
          </div>
        </div>
      )}

      <ConfirmModal
        isOpen={showForceRegenConfirm}
        title="Force-regenerate periods with data?"
        body={
          <div className="space-y-2 text-fieldiq-text">
            <p>The following periods contain timesheet data that will be permanently deleted:</p>
            <ul className="list-disc list-inside text-fieldiq-text">
              {skippedPeriods.map(s => (
                <li key={s.period_id}>
                  Period #{s.period_number}
                  {s.counts && Object.entries(s.counts).filter(([, c]) => c > 0).length > 0 && (
                    <span className="text-fieldiq-muted"> — {Object.entries(s.counts).filter(([, c]) => c > 0).map(([t, c]) => `${c} ${t.replace(/_/g, ' ')}`).join(', ')}</span>
                  )}
                </li>
              ))}
            </ul>
            <p className="text-fieldiq-text font-semibold">This action cannot be undone.</p>
          </div>
        }
        confirmLabel="Delete data and regenerate"
        confirmVariant="destructive"
        loading={forceRegenerating}
        onConfirm={handleForceRegenerate}
        onCancel={() => setShowForceRegenConfirm(false)}
      />
    </SettingsPage>
  );
}
